import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import useCurrentLang from '@/hooks/apps/useCurrentLang';

import {
  ABOUT_MILESTONES_BY_LOCALE,
  type AboutMilestone,
} from '@/data/shared/aboutCopy';

import Area from '@/components/apps/layouts/shared/Area';

/**
 * ### groupByYear
 *
 * Splits the record into years, keeping the order it arrives in. The record
 * is already newest first, so the years come out newest first too and the
 * heading for each is simply the year of its first entry.
 *
 * @param milestones The record, newest first
 * @returns One entry per year, each with the milestones under it
 */
function groupByYear(
  milestones: ReadonlyArray<AboutMilestone>,
): Array<{ year: string; entries: Array<AboutMilestone> }> {
  const years: Array<{ year: string; entries: Array<AboutMilestone> }> = [];

  for (const milestone of milestones) {
    const year = milestone.period.slice(0, 4);
    const current = years.at(-1);
    if (current?.year === year) current.entries.push(milestone);
    else years.push({ year, entries: [milestone] });
  }

  return years;
}

/**
 * ### AboutMilestonesSection
 *
 * The record of where the sound AI has been put to work, newest first, on a
 * rail with a marker for every entry and the year called out beside it —
 * above it, on a screen too narrow to set the two side by side.
 *
 * The whole record is on the page from the start. It is long, but a record
 * that hides most of itself behind a press reads as a shorter record, which
 * is the opposite of what it is here to say.
 *
 * @returns The rendered section element
 * @see {@link ABOUT_MILESTONES_BY_LOCALE} The record rendered here
 */
export default function AboutMilestonesSection() {
  const { t } = useTranslation('common');
  const currentLang = useCurrentLang();
  const milestones = ABOUT_MILESTONES_BY_LOCALE[currentLang];
  const lastEntry = milestones.at(-1);

  return (
    <section
      className="bg-apps-coreGrayWhite"
      data-analytics-section="about_milestones"
    >
      <Area>
        <div className="flex flex-col gap-12 py-[96px] lg:gap-16 lg:py-[clamp(120px,11.111vw,180px)]">
          <div className="max-w-[900px]">
            <p className="text-[13px] font-bold uppercase leading-[140%] tracking-[0.65px] text-apps-corePrimary01 lg:text-[15px]">
              Track Record
            </p>
            <h2 className="mt-4 text-balance text-[30px] font-bold leading-[134%] tracking-[-0.6px] text-apps-coreGrayBlack sm:text-[36px] lg:mt-6 lg:text-[clamp(42px,3.611vw,56px)] lg:leading-[126%] lg:tracking-[-1.12px]">
              {t('소리 AI가 현장에 적용되어 온 기록')}
            </h2>
          </div>

          <ol className="flex flex-col">
            {groupByYear(milestones).map(({ year, entries }) => (
              <li key={year} className="group/year">
                <div className="grid grid-cols-1 lg:grid-cols-[120px_minmax(0,1fr)] lg:gap-10">
                  {/*
                   * The year has to sit on the same baseline as the first
                   * entry beside it, and it is set much larger, so its top
                   * padding is what is left after its own ascent. Stated in
                   * em so it keeps up with the clamped font size instead of
                   * drifting as the viewport changes.
                   *
                   * Narrow enough and the year drops above its entries
                   * instead of standing beside them, which would leave the
                   * rail broken at every year. So there it hangs off the
                   * rail like an entry does and carries its own length of
                   * it, and the line runs unbroken from 2026 to 2017.
                   *
                   * Stacked, every year after the first also needs room
                   * above it, or it lands on the last entry of the year
                   * before. The room is a spacer of its own carrying a
                   * length of the rail, not padding on the year: the year
                   * sticks while its entries scroll, and padding would
                   * stick with it as an empty band under the header.
                   *
                   * The stuck year sits right under the mobile header
                   * (60px) while it shows and moves to the top when it
                   * slides away; `useHeader` publishes which on <html>.
                   */}
                  <span
                    className="relative block h-8 group-first/year:hidden lg:hidden"
                    aria-hidden="true"
                  >
                    <span className="absolute left-0 top-0 h-full w-px bg-apps-coreGray06" />
                  </span>
                  <p className="sticky top-0 z-10 bg-apps-coreGrayWhite pb-2 pl-8 font-dmSans text-[28px] font-bold leading-[110%] tracking-[-0.56px] text-apps-coreGrayBlack transition-[top] duration-300 motion-reduce:transition-none max-lg:[html:not([data-header-visible=false])_&]:top-[60px] lg:top-[140px] lg:z-auto lg:self-start lg:bg-transparent lg:pb-0 lg:pl-0 lg:pt-[calc(45px-0.85em)] lg:text-[clamp(32px,2.778vw,40px)]">
                    <span
                      className="absolute left-0 top-0 h-full w-px bg-apps-coreGray06 lg:hidden"
                      aria-hidden="true"
                    />
                    {year}
                  </p>
                  <ol className="flex flex-col">
                    {entries.map((entry) => (
                      <li
                        key={`${entry.period}-${entry.customer}-${entry.work}`}
                        className="group relative grid grid-cols-1 gap-1 py-5 pl-8 lg:grid-cols-[72px_minmax(0,220px)_minmax(0,1fr)] lg:items-baseline lg:gap-6 lg:py-7 lg:last:pb-7 lg:pl-10"
                      >
                        {/*
                         * The rail is drawn on each entry and runs its whole
                         * height, so entries and the years that hold them
                         * stack into one unbroken line. Only the last entry
                         * of the record stops, at its own marker, rather
                         * than trailing off the end.
                         */}
                        <span
                          className={`absolute left-0 top-0 w-px bg-apps-coreGray06 ${
                            entry === lastEntry
                              ? 'h-[30px] lg:h-[38px]'
                              : 'h-full'
                          }`}
                          aria-hidden="true"
                        />
                        <span
                          className="absolute -left-1 top-[30px] h-[9px] w-[9px] rounded-full border-2 border-apps-coreGrayWhite bg-apps-coreGray04 transition-colors duration-300 group-hover:bg-apps-corePrimary01 lg:top-[38px]"
                          aria-hidden="true"
                        />
                        <p className="text-[13px] font-bold leading-[150%] tracking-[-0.26px] text-apps-coreGray02 [font-variant-numeric:tabular-nums] lg:text-[15px]">
                          {entry.period.slice(5)}
                        </p>
                        <p className="text-[18px] font-bold leading-[145%] tracking-[-0.36px] text-apps-coreGrayBlack lg:text-[20px]">
                          {entry.customer}
                        </p>
                        <p className="text-pretty text-[15px] font-medium leading-[160%] tracking-[-0.3px] text-apps-coreGray01 lg:text-[17px]">
                          {entry.work}
                        </p>
                      </li>
                    ))}
                  </ol>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Area>
    </section>
  );
}
